/**
 * Right-edge scroll-overflow detection for the table tile's horizontally
 * scrollable body (UX review finding #4). Native/overlay scrollbars (macOS
 * "when scrolling" style especially) can be invisible at rest, silently
 * hiding truncated trailing columns with no cue — `TableTileView` renders a
 * right-edge fade while `useTileScrollFade` reports there's more content past
 * the current scroll position.
 *
 * `hasMoreContentRight` is exported standalone (pure, no DOM) for direct unit
 * coverage: jsdom never lays out real `scrollWidth`/`clientWidth`, so the
 * hook's effect wiring can't be exercised meaningfully in a component test.
 * Same 1px edge tolerance and `useLayoutEffect` (avoids a one-frame flash of
 * the wrong fade state) as the existing `FkBreadcrumb` scroll-fade affordance
 * — one shared convention for "is there more to scroll to" across the app.
 */
import { useLayoutEffect, useState, type RefObject } from 'react';

const SCROLL_EDGE_EPSILON = 1;

export function hasMoreContentRight(el: {
  scrollWidth: number;
  clientWidth: number;
  scrollLeft: number;
}): boolean {
  return el.scrollWidth - el.clientWidth - el.scrollLeft > SCROLL_EDGE_EPSILON;
}

/**
 * Re-measured on scroll, on container resize, and whenever `deps` change (a
 * column drag-resize or the column set itself changes the table's own
 * content width without necessarily resizing or scrolling the container).
 */
export function useTileScrollFade(ref: RefObject<HTMLElement | null>, deps: readonly unknown[]): boolean {
  const [showRightFade, setShowRightFade] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setShowRightFade(hasMoreContentRight(el));
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return showRightFade;
}
